/**
 * Security Utilities
 * Provides encryption and security functions for sensitive data
 */

/**
 * Encode string to base64 with UTF-8 support
 * @param {string} str - String to encode
 * @returns {string} Base64 encoded string
 */
const encodeBase64 = (str) => {
  try {
    // Use encodeURIComponent to handle Unicode characters properly
    // Then convert to base64
    return btoa(unescape(encodeURIComponent(str)));
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('Base64 encoding error:', error);
    }
    // Fallback: try direct btoa (may fail for non-Latin1)
    try {
      return btoa(str);
    } catch (e) {
      // If all fails, return original string
      return str;
    }
  }
};

/**
 * Decode base64 string with UTF-8 support
 * @param {string} str - Base64 encoded string
 * @returns {string} Decoded string
 */
const decodeBase64 = (str) => {
  try {
    // Decode base64 first, then handle UTF-8
    return decodeURIComponent(escape(atob(str)));
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('Base64 decoding error:', error);
    }
    // Fallback: try direct atob (may fail for non-Latin1)
    try {
      return atob(str);
    } catch (e) {
      // If all fails, return original string
      return str;
    }
  }
};

/**
 * Simple encryption for localStorage (not for production-grade security)
 * For production, use proper encryption libraries or server-side storage
 * @param {string} text - Text to encrypt
 * @param {string} key - Encryption key (should be stored securely)
 * @returns {string} Encrypted text
 */
export const simpleEncrypt = (text, key) => {
  if (!text || !key) return text;
  
  try {
    // Simple XOR encryption (not cryptographically secure, but better than plaintext)
    let result = '';
    for (let i = 0; i < text.length; i++) {
      const charCode = text.charCodeAt(i) ^ key.charCodeAt(i % key.length);
      result += String.fromCharCode(charCode);
    }
    // Base64 encode to make it safe for storage (with UTF-8 support)
    return encodeBase64(result);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('Encryption error:', error);
    }
    return text; // Fallback to plaintext if encryption fails
  }
};

/**
 * Check if a string looks like valid base64 (without trying to decode it)
 * @param {string} str - String to check
 * @returns {boolean} True if looks like base64
 */
const isValidBase64 = (str) => {
  if (!str || typeof str !== 'string') return false;
  
  // Base64 strings should only contain A-Z, a-z, 0-9, +, /, and = for padding
  // Check if the string contains only valid base64 characters
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
  if (!base64Regex.test(str)) {
    return false;
  }
  
  // For very long strings, we can't check the exact length requirement efficiently
  // But if it passes the regex, it's likely base64
  // Only check length for shorter strings to avoid performance issues
  if (str.length < 1000) {
    // Check length - base64 strings should have length that's a multiple of 4
    // (after accounting for padding)
    const paddingCount = (str.match(/=/g) || []).length;
    const baseLength = str.length - paddingCount;
    if (baseLength % 4 !== 0 && baseLength > 0) {
      return false;
    }
  }
  
  // If it passes all checks, it looks like base64
  return true;
};

/**
 * Simple decryption for localStorage
 * @param {string} encryptedText - Encrypted text
 * @param {string} key - Decryption key
 * @returns {string} Decrypted text
 */
export const simpleDecrypt = (encryptedText, key) => {
  if (!encryptedText || !key) return encryptedText;
  
  // Check if the text looks like base64 (encrypted) or plain JSON
  // If it doesn't look like base64, it's probably plain JSON
  const isValid = isValidBase64(encryptedText);
  
  // If it looks like base64, try to decrypt it
  if (isValid) {
    try {
      // Base64 decode first (with UTF-8 support)
      const decoded = decodeBase64(encryptedText);
      
      let result = '';
      for (let i = 0; i < decoded.length; i++) {
        const charCode = decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length);
        result += String.fromCharCode(charCode);
      }
      return result;
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error('[security] simpleDecrypt: Error during decryption:', error);
      }
      // If decryption fails, return original text (might be plain JSON)
      return encryptedText;
    }
  }
  
  // If it doesn't look like base64, but it's not valid JSON either, 
  // it might still be encrypted (maybe the base64 check failed incorrectly)
  // Try to decode it anyway if it's long enough to be encrypted
  if (encryptedText.length > 100) {
    try {
      const decoded = decodeBase64(encryptedText);
      
      let result = '';
      for (let i = 0; i < decoded.length; i++) {
        const charCode = decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length);
        result += String.fromCharCode(charCode);
      }
      return result;
    } catch (error) {
      // If decryption fails, return original text (might be plain JSON)
      return encryptedText;
    }
  }
  
  // Not base64 and not long enough, return as-is (probably plain JSON)
  return encryptedText;
};

/**
 * Get encryption key from environment or generate a default
 * In production, this should be stored securely (not in localStorage)
 */
const getEncryptionKey = () => {
  // Use environment variable if available, otherwise use a default (not secure for production)
  // Vite uses import.meta.env instead of process.env
  try {
    const env = import.meta.env || {};
    const key = env.VITE_ENCRYPTION_KEY || env.REACT_APP_ENCRYPTION_KEY;
    
    // In production, warn if using default key (but don't break the app)
    if (process.env.NODE_ENV === "production") {
      if (!key || key === 'hotelsriver-default-key-change-in-production') {
        // Warn but don't throw - allow app to continue with default key
        // In future, consider requiring encryption key
        if (process.env.NODE_ENV === "development") {
          console.warn('[security] Using default encryption key in production is not secure. Set VITE_ENCRYPTION_KEY environment variable.');
        }
      }
    }
    
    return key || 'hotelsriver-default-key-change-in-production';
  } catch (e) {
    // Fallback if import.meta is not available
    if (process.env.NODE_ENV === "development") {
      console.warn('[security] Could not access environment variables, using default key');
    }
    return 'hotelsriver-default-key-change-in-production';
  }
};

/**
 * Encrypt sensitive data before storing in localStorage
 * @param {any} data - Data to encrypt
 * @returns {string} Encrypted JSON string
 */
export const encryptForStorage = (data) => {
  try {
    const jsonString = JSON.stringify(data);
    const key = getEncryptionKey();
    return simpleEncrypt(jsonString, key);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('Error encrypting data:', error);
    }
    return JSON.stringify(data); // Fallback to plain JSON
  }
};

/**
 * Check if a string looks like JSON (safe to pass to JSON.parse attempt).
 * Avoids parsing encrypted payloads that start with e.g. "E00XAA..." or base64.
 * @param {string} str - String to check
 * @returns {boolean} True if string looks like JSON
 */
const looksLikeJson = (str) => {
  if (!str || typeof str !== 'string') return false;
  const trimmed = str.trim();
  if (!trimmed.length) return false;
  const first = trimmed[0];
  // JSON: object, array, string (quoted), or primitive
  if (first === '{' || first === '[' || first === '"') return true;
  // Primitives: number, true, false, null
  if (/^[-0-9]/.test(trimmed) || trimmed === 'true' || trimmed === 'false' || trimmed === 'null') return true;
  return false;
};

/**
 * Safely parse string as JSON; returns null on failure (no throw).
 * @param {string} str - String to parse
 * @returns {any|null} Parsed value or null
 */
const safeJsonParse = (str) => {
  if (str === undefined || str === null || typeof str !== 'string') return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

/**
 * Decrypt data from localStorage.
 * Handles: plain JSON, encrypted strings (e.g. "E00XAA..."), null/undefined, malformed data.
 * Does not call JSON.parse on encrypted payloads (avoids SyntaxError).
 * @param {string} encryptedData - Encrypted data string or plain JSON string
 * @returns {any} Decrypted data object, or raw string, or null on failure
 */
export const decryptFromStorage = (encryptedData) => {
  if (encryptedData === null || encryptedData === undefined) return null;
  if (typeof encryptedData !== 'string') return null;
  const trimmed = encryptedData.trim();
  if (!trimmed.length) return null;

  try {
    // Only try JSON.parse when input looks like JSON (backward compatibility for plain JSON)
    if (looksLikeJson(encryptedData)) {
      const parsed = safeJsonParse(encryptedData);
      if (parsed !== null && typeof parsed === 'object') return parsed;
      if (parsed !== null) return parsed; // number, boolean, string
    }

    // Input looks like encrypted (e.g. starts with E, or base64) — decrypt without parsing first
    const key = getEncryptionKey();
    const decrypted = simpleDecrypt(encryptedData, key);

    if (decrypted === null || decrypted === undefined) return null;
    if (typeof decrypted !== 'string') return decrypted;

    const decryptedTrimmed = decrypted.trim();
    if (!decryptedTrimmed.length) return null;

    // After decrypt: if result looks like JSON, parse it; otherwise return raw string
    if (looksLikeJson(decrypted)) {
      const parsed = safeJsonParse(decrypted);
      if (parsed !== null) return parsed;
    }
    return decrypted;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[security] decryptFromStorage: Error:', error);
    }
    return null;
  }
};

/**
 * Sanitize user input to prevent XSS
 * @param {string} input - User input string
 * @returns {string} Sanitized string
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove potentially dangerous characters and patterns
  return input
    .replace(/[<>]/g, '') // Remove < and > to prevent HTML injection
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers like onclick=
    .replace(/data:/gi, '') // Remove data: protocol
    .trim();
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email
 */
export const isValidEmail = (email) => {
  if (typeof email !== 'string') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Generate CSRF token (simple implementation)
 * In production, use proper CSRF token generation
 * @returns {string} CSRF token
 */
export const generateCSRFToken = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Store CSRF token in sessionStorage
 */
export const storeCSRFToken = () => {
  const token = generateCSRFToken();
  sessionStorage.setItem('csrf_token', token);
  return token;
};

/**
 * Get CSRF token from sessionStorage
 * @returns {string|null} CSRF token or null
 */
export const getCSRFToken = () => {
  return sessionStorage.getItem('csrf_token');
};

